import { Button, ButtonMenu, Css, FullBleed, SelectField, Tag, useModal, useTestIds } from "@homebound/beam";
import { useHistory } from "react-router-dom";
import { createBidPackagePublishUrl, createBidPackagesDetailUrl, createBidPackagesUrl } from "src/RouteUrls";
import { FormattedDate, emptyCellDash } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { useFullscreenModal } from "src/components/useFullscreenModal";
import { BidPackageStatus } from "src/generated/graphqlTypes";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PageHeaderBreadcrumbs } from "src/routes/layout/PageHeader";
import { StringParam, useQueryParams } from "use-query-params";
import { useSaveBidPackageMutation } from "../publish-bid-package/PublishBidPackagePage.generated";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { AwardBidModal } from "./AwardBidModal";
import { UpdatesAvailableButton } from "./BidUpdatesAvailable";
import { DownloadBidModal } from "./DownloadBidModal";
import { EditBidPackageModal } from "./EditBidPackageModal";

type BidPackageHeaderProps = {
  bidPackage: PageBidPackageDetailFragment;
  displayingOldVersion?: boolean;
};

export function BidPackageHeader({ bidPackage, displayingOldVersion = false }: BidPackageHeaderProps) {
  const {
    id,
    name,
    developments,
    projects,
    bidPackageGroup,
    status,
    dueDate,
    estimatedConstructionDate,
    availableUpdates,
  } = bidPackage;
  const tid = useTestIds({});
  const { openFullscreen } = useFullscreenModal();
  const { openModal } = useModal();
  const [saveBidPackage] = useSaveBidPackageMutation({
    refetchQueries: [BidPackageDetailPageDocument],
  });
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;
  const history = useHistory();

  const handleArchive = (archive: boolean) => {
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to {archive ? "archive" : "unarchive"} the bid package <b>{name}</b>?
            </>
          }
          onConfirmAction={async () => {
            await saveBidPackage({
              variables: {
                input: {
                  id: bidPackage.id,
                  archive,
                },
              },
            });
          }}
          title={archive ? "Archive Bid Package" : "Unarchive Bid Package"}
          label={archive ? "Archive" : "Unarchive"}
        />
      ),
    });
  };

  return (
    <FullBleed>
      <header css={Css.df.aic.py2.mhPx(64).bb.bw1.bcGray200.bgWhite.ifMdAndDown.fdc.$}>
        <div>
          <PageHeaderBreadcrumbs
            breadcrumb={[
              { href: createBidPackagesUrl(), label: "Bids" },
              {
                href: createBidPackagesUrl(developments.first?.id, projects.first?.id),
                label: `${developments.first?.name || projects.first?.name}`,
              },
              {
                // TODO: Make bidPackageGroup required in schema on the BE (instead of forcing it here)
                // since this will always exist as a bidPackage parent
                href: createBidPackagesDetailUrl(bidPackageGroup!.id, id),
                label: name,
              },
            ]}
            linkXss={Css.blue700.cursorPointer.$}
          />

          {/** Customize our own header since the pageHeader component limits the ability to render header styles per the figma */}
          <div css={Css.dg.gtc("8fr 4fr 4.5fr 1fr").aic.$}>
            <h1 css={Css.xl.$} {...tid.name}>
              {name}
            </h1>

            <div css={Css.df.fdr.gap5.$}>
              <div css={Css.df.fdc.wsnw.$}>
                <div css={Css.xs.gray700.gap2.$}>Status</div>
                <div css={Css.df.fdr.aic.gap1.$}>
                  <StatusIndicator status={status.code} type="BidPackage" />
                  <div {...tid.status}>{status.name}</div>
                </div>
              </div>

              <div css={Css.bl.br.gray200.$} />

              <div css={Css.df.fdc.wsnw.$}>
                <div css={Css.xs.gray700.$}>Bid Due Date</div>
                {/* todo confirm historical siutation check style */}
                {dueDate ? (
                  <FormattedDate date={dueDate} xss={Css.mdSb.$} />
                ) : bidPackage.isInternalEstimate ? (
                  <div css={Css.mdSb.$}>Internal Estimate</div>
                ) : (
                  <div css={Css.mdSb.$}>Historical</div>
                )}
              </div>

              <div css={Css.bl.br.gray200.$} />

              <div css={Css.df.fdc.wsnw.$}>
                <div css={Css.xs.gray700.$}>Est Work Start</div>
                {estimatedConstructionDate ? (
                  <FormattedDate date={estimatedConstructionDate} xss={Css.mdSb.$} />
                ) : (
                  emptyCellDash
                )}
              </div>
            </div>
            <div />
            <div css={Css.df.fdr.gap2.aic.$}>
              {bidPackage.archivedAt ? (
                <div css={Css.mwPx(100).$}>
                  <Tag text="Archived" type="warning" />
                </div>
              ) : (
                <UpdatesAvailableButton bidPackageId={id} updates={availableUpdates} />
              )}
              <BidPackageVersionSelect bidPackage={bidPackage} />

              <Button
                disabled={
                  displayingOldVersion ||
                  (isDraft ? undefined : disableBasedOnPotentialOperation(bidPackage.canComplete))
                }
                label={isDraft ? "Publish" : "Award Bid"}
                variant="primary"
                onClick={async () => {
                  if (isDraft) {
                    if (bidPackage.isInternalEstimate || bidPackage.isHistoricalBid) {
                      await saveBidPackage({
                        variables: {
                          input: {
                            id: bidPackage.id,
                            status: BidPackageStatus.Open,
                            versionRequests: bidPackage.activeRequests.map((r) => ({
                              bidPackageRequestId: r.id,
                              notify: false,
                            })),
                          },
                        },
                      });
                    } else {
                      history.push(createBidPackagePublishUrl(bidPackageGroup!.id, id));
                    }
                  } else {
                    openFullscreen(<AwardBidModal bidPackageId={id} />);
                  }
                }}
              />
              <ButtonMenu
                placement="right"
                trigger={{ icon: "verticalDots" }}
                items={[
                  {
                    label: "Edit Bid",
                    onClick: () => openFullscreen(<EditBidPackageModal bidPackage={bidPackage} />, true),
                    disabled: displayingOldVersion,
                  },
                  {
                    label: "Add Trade",
                    onClick: () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> }),
                    disabled: displayingOldVersion,
                  },
                  ...(!bidPackage.isInternalEstimate
                    ? [
                        {
                          label: "Download Bid",
                          onClick: () => openModal({ content: <DownloadBidModal bidPackage={bidPackage} /> }),
                        },
                      ]
                    : []),
                  {
                    label: bidPackage.archivedAt ? "Unarchive Bid" : "Archive Bid",
                    onClick: () => handleArchive(!bidPackage.archivedAt),
                    disabled: displayingOldVersion,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </header>
    </FullBleed>
  );
}

function BidPackageVersionSelect({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const [{ versionId }, setQueryParams] = useQueryParams({
    versionId: StringParam,
  });
  return (
    <div css={Css.wPx(130).px1.$}>
      <SelectField
        label="version"
        value={versionId ?? bidPackage.latestVersion.id}
        options={bidPackage.versions}
        getOptionValue={(v) => v.id}
        getOptionLabel={(v) => v.displayVersion}
        onSelect={(versionId) => {
          setQueryParams({ versionId: versionId !== bidPackage.versions.last?.id ? versionId : undefined });
        }}
        labelStyle="hidden"
        borderless
        autoSort={false}
      />
    </div>
  );
}
